import type { CartLine } from './cart';
import { subtotalCents, meetsDeliveryMinimum } from './cart';
import { supabase } from './supabase';

/**
 * Logique de checkout (US-505) — retrait / livraison, min commande, frais, validation.
 * Pur et testable. Couvre les critères de non-régression #11 et #12.
 */

export type PickupMode = 'pickup' | 'delivery';

export interface DeliveryConfig {
  enabled: boolean;
  feeCents: number;
  minCents: number;
}

/** Longueur max de la remarque client (parité legacy §F-23). */
export const CUSTOMER_NOTE_MAX = 300;

/**
 * Frais de service Lil & Co — 0,99 flat dès que le panier n'est pas vide (parité legacy C11).
 * Legacy : `const feeForBasket = () => 0.99` puis `subtotal > 0 ? feeForBasket(...) : 0`,
 * identique en € (Big Burger) et CHF (Food Kourt). Montant en centimes.
 */
export const SERVICE_FEE_CENTS = 99;

/** Frais de service appliqué : 0,99 (99 cts) si panier non vide, sinon 0. */
export function serviceFeeFor(lines: CartLine[]): number {
  return subtotalCents(lines) > 0 ? SERVICE_FEE_CENTS : 0;
}

export interface CheckoutState {
  mode: PickupMode;
  slot: string | null; // créneau choisi
  paymentMethod: 'online' | 'onsite' | null;
  /** Coordonnées client (parité legacy) : nom + email (reçu) + mobile (contact livraison). */
  customerName?: string;
  email?: string;
  phone?: string;
  /** Adresse de livraison (mode livraison) : rue, NPA/code postal, ville. */
  deliveryStreet?: string;
  deliveryPostal?: string;
  deliveryCity?: string;
  /** Remarque libre du client (≤ CUSTOMER_NOTE_MAX car.) → orders.customer_note. */
  note?: string;
  /** Consentements marketing (RGPD, opt-in, décochés par défaut) : offres resto + offres réseau. */
  marketingConsent?: boolean;
  networkConsent?: boolean;
  /** Accord pour le suivi de commande (WhatsApp / SMS / email) — porté par la MENTION
   *  affichée sous le bouton de commande, pas par une case à cocher : envoyer à un client
   *  l'état de SA commande relève de l'exécution du contrat (RGPD), et Meta accepte depuis
   *  nov. 2024 un opt-in général nommant l'entreprise.
   *  ⚠️ NE PAS confondre avec `marketingConsent` / `networkConsent` (finalités distinctes,
   *  cases décochées) : les fusionner rendrait TOUS les consentements invalides. */
  whatsappConsent?: boolean;
  /** Acceptation des CGV — OBLIGATOIRE : sans elle, la commande ne se valide pas.
   *  Case décochée par défaut (une case pré-cochée ne vaut pas acceptation). */
  termsAccepted?: boolean;
  /** Frais de livraison effectifs (zone choisie, parité C5) — repris pour la commande. */
  deliveryFeeCents?: number;
  /** Minimum effectif de la zone choisie. */
  deliveryMinCents?: number;
  /** Code promo appliqué (validé côté serveur) → orders.promo_code ; la remise est
   *  RECALCULÉE côté serveur au paiement (jamais confiance au navigateur). */
  promoCode?: string | null;
  /** Réduction affichée (aperçu serveur) — indicatif pour l'UI uniquement. */
  promoDiscountCents?: number;
}

/** Email basique mais robuste (un @ + un point dans le domaine). Pur. */
export function isValidEmail(email: string | null | undefined): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email ?? '').trim());
}

/** Normalise un numéro : retire espaces/séparateurs, `00`→`+`. Pur. */
export function normalizePhone(phone: string | null | undefined): string {
  return (phone ?? '').trim().replace(/[\s.\-()]/g, '').replace(/^00/, '+');
}

/** Mobile FR (06/07 + 8 chiffres) OU CH (07[5-9] + 7 chiffres) — `+33`/`+41`/`0` tolérés. Pur. */
export function isValidMobileFRCH(phone: string | null | undefined): boolean {
  const n = normalizePhone(phone);
  return /^(?:\+33|0)[67]\d{8}$/.test(n) || /^(?:\+41|0)7[5-9]\d{7}$/.test(n);
}

/** Nom client valide (≥ 2 caractères, hors espaces). Pur. */
export function isValidCustomerName(name: string | null | undefined): boolean {
  return (name ?? '').trim().length >= 2;
}

/** Normalise une remarque client : trim + plafond de longueur ; vide → null. */
export function normalizeCustomerNote(note: string | null | undefined): string | null {
  const cleaned = (note ?? '').trim().slice(0, CUSTOMER_NOTE_MAX);
  return cleaned.length > 0 ? cleaned : null;
}

/** Modes disponibles selon la config du tenant (critère #11 : livraison seulement si activée). */
export function availableModes(delivery: DeliveryConfig): PickupMode[] {
  return delivery.enabled ? ['pickup', 'delivery'] : ['pickup'];
}

/** Frais appliqués (uniquement en livraison — critère #12). */
export function deliveryFeeFor(mode: PickupMode, delivery: DeliveryConfig): number {
  return mode === 'delivery' ? delivery.feeCents : 0;
}

/** Total final en centimes (sous-total + frais de service Lil & Co + frais de livraison éventuels). */
export function checkoutTotalCents(
  lines: CartLine[],
  mode: PickupMode,
  delivery: DeliveryConfig,
): number {
  return subtotalCents(lines) + serviceFeeFor(lines) + deliveryFeeFor(mode, delivery);
}

export interface CheckoutValidation {
  ok: boolean;
  reasons: string[];
}

/**
 * Valide qu'une commande peut être passée.
 * Bloque si : panier vide · pas de créneau · pas de paiement · CGV non acceptées ·
 * (livraison) minimum non atteint.
 *
 * ⚠️ Les CGV sont OBLIGATOIRES : pas d'acceptation = pas de commande. C'est licite,
 * ce sont les conditions du contrat de vente.
 * ⚠️ En revanche, on n'y glisse JAMAIS les consentements marketing : conditionner
 * l'achat à une acceptation de prospection rend ce consentement invalide (RGPD :
 * il doit être « librement donné »). Les cases marketing restent séparées et décochées.
 */
export function validateCheckout(
  lines: CartLine[],
  state: CheckoutState,
  delivery: DeliveryConfig,
): CheckoutValidation {
  const reasons: string[] = [];
  if (lines.length === 0) reasons.push('Panier vide');
  if (!isValidCustomerName(state.customerName)) reasons.push('Nom requis (2 caractères min.)');
  if (!isValidEmail(state.email)) reasons.push('Email invalide');
  if (!isValidMobileFRCH(state.phone)) reasons.push('Téléphone mobile invalide (FR 06/07 ou CH 07x)');
  if (!state.slot) reasons.push('Aucun créneau sélectionné');
  if (!state.paymentMethod) reasons.push('Mode de paiement non choisi');
  if (state.termsAccepted !== true) reasons.push("Vous devez accepter les conditions générales de vente");
  if (state.mode === 'delivery') {
    if (!delivery.enabled) reasons.push('Livraison non disponible');
    else if (!meetsDeliveryMinimum(lines, delivery.minCents))
      reasons.push('Minimum de commande livraison non atteint');
  }
  return { ok: reasons.length === 0, reasons };
}

/** Entrée pour passer une vraie commande (insertion base). */
export interface PlaceOrderInput {
  tenantId: string;
  lines: CartLine[];
  mode: PickupMode;
  delivery: DeliveryConfig;
  customerName?: string;
  /** Email client (reçu) → orders.customer_email. */
  email?: string | null;
  /** Mobile client (contact livraison) → orders.customer_phone. */
  phone?: string | null;
  /** Créneau choisi, format `${dayKey}T${HH:MM}` (issu du SlotPicker) ; null = retrait au plus tôt. */
  slot?: string | null;
  /** Paiement choisi au checkout ; défaut = sur place (le paiement en ligne sera branché avec le PSP). */
  paymentMethod?: 'online' | 'onsite';
  /** Remarque libre du client → orders.customer_note (parité legacy §F-23). */
  note?: string | null;
  /** Point de commande (kiosque QR : Comptoir / Table N / Drive…) → orders.order_point. */
  orderPoint?: string | null;
  /** Code promo appliqué (validé serveur) → orders.promo_code ; la remise est
   *  consommée + recalculée côté serveur au paiement (RPC redeem_promo_code). */
  promoCode?: string | null;
  /** Opt-in WhatsApp (case du checkout) → orders.whatsapp_consent. Sans lui, l'Edge
   *  `send-whatsapp` n'envoie RIEN : Meta exige un accord même pour un template UTILITY. */
  whatsappConsent?: boolean;
  /** Acceptation EXPRESSE des CGV (case obligatoire) → orders.terms_accepted.
   *  Art. 1119 C. civ. : sans acceptation prouvée, les CGV sont INOPPOSABLES. La date est
   *  posée par un trigger SQL — c'est elle, la preuve, pas la case. */
  termsAccepted?: boolean;
}

/** Date du jour au format ISO `YYYY-MM-DD` (fuseau local). */
function todayIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Décompose un créneau `${dayKey}T${HH:MM}` en (pickup_date, pickup_time) — colonnes
 * NOT NULL de `orders`. Tolérant : sans date valide → aujourd'hui ; sans heure valide → 12:00:00.
 */
export function parseSlot(slot: string | null | undefined): {
  pickupDate: string;
  pickupTime: string;
} {
  let datePart = '';
  let timePart = '';
  if (slot && slot.includes('T')) {
    [datePart, timePart] = slot.split('T');
  } else if (slot) {
    timePart = slot;
  }
  const pickupDate = /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : todayIso();
  const pickupTime = /^\d{1,2}:\d{2}$/.test(timePart) ? `${timePart.padStart(5, '0')}:00` : '12:00:00';
  return { pickupDate, pickupTime };
}

/**
 * order_number lisible & sans collision via la fonction Postgres `generate_order_number`
 * (compte par tenant, ex. `AT-2026-00042`). Repli défensif local si la RPC échoue,
 * pour ne jamais bloquer une commande.
 */
async function nextOrderNumber(tenantId: string): Promise<string> {
  const { data, error } = await supabase.rpc('generate_order_number', { p_tenant_id: tenantId });
  if (!error && typeof data === 'string' && data.length > 0) return data;
  console.warn('[checkout] generate_order_number indisponible, repli local:', error?.message);
  return `XX-${new Date().getFullYear()}-${Date.now().toString().slice(-5)}`;
}

/**
 * Enregistre une vraie commande en base : insère `orders` puis `order_items`
 * (mappés vers les vraies colonnes `product_name`/`quantity`) → la commande arrive
 * sur la tablette cuisine via Realtime. Renvoie l'id créé, ou null si rien à
 * commander / échec. I/O isolée ; le calcul du total reste pur (checkoutTotalCents).
 *
 * Le payload colle au schéma RÉEL de `orders` (enum FR `status`/`payment_status`,
 * `order_number` + `pickup_date`/`pickup_time` NOT NULL) — cf. sites legacy en prod.
 */
export interface PlacedOrder {
  id: string;
  orderNumber: string;
  totalCents: number;
  pickupTime: string;
  pickupDate: string;
}

export async function placeOrder(input: PlaceOrderInput): Promise<PlacedOrder | null> {
  const { tenantId, lines, mode, delivery, customerName, email, phone, slot, paymentMethod, note, orderPoint, promoCode, whatsappConsent, termsAccepted } =
    input;
  if (lines.length === 0) return null;
  const total = checkoutTotalCents(lines, mode, delivery);
  if (total <= 0) return null;

  let orderNumber = await nextOrderNumber(tenantId);
  const { pickupDate, pickupTime } = parseSlot(slot);
  const isOnline = paymentMethod === 'online';
  const customerNote = normalizeCustomerNote(note);

  const payload: Record<string, unknown> = {
    tenant_id: tenantId,
    order_number: orderNumber,
    customer_name: customerName ?? null,
    customer_email: (email ?? '').trim() || null,
    customer_phone: normalizePhone(phone) || null,
    // Accord « suivi de commande » : donné en passant commande (mention sous le bouton).
    // Par défaut TRUE — mais explicitement révocable (un client qui répond STOP, ou qu'on
    // désinscrit, passe à false et ne reçoit plus rien). L'Edge le relit EN BASE avant
    // tout envoi ; l'horodatage est posé par un trigger SQL, infalsifiable côté navigateur.
    whatsapp_consent: whatsappConsent !== false,
    // PREUVE d'acceptation des CGV (art. 1119 C. civ.). `validateCheckout` refuse déjà la
    // commande sans la case cochée ; on TRACE ici l'acceptation, sinon la case ne prouve rien.
    terms_accepted: termsAccepted === true,
    pickup_date: pickupDate,
    pickup_time: pickupTime,
    service_type: 'takeaway',
    status: 'recue',
    subtotal_cents: subtotalCents(lines),
    service_fee_cents: serviceFeeFor(lines),
    total_cents: total,
    pickup_mode: mode,
    delivery_fee_cents: deliveryFeeFor(mode, delivery),
    payment_method: isOnline ? 'online' : 'onsite',
    payment_status: isOnline ? 'pending' : 'onsite_pending',
    customer_note: customerNote,
  };
  // N'ajoute `order_point` (kiosque) QUE s'il est fourni — la colonne n'existe
  // qu'après la migration SQL_P2 ; on ne casse jamais le flux normal sans point.
  const point = (orderPoint ?? '').trim();
  if (point) payload.order_point = point;
  // Code promo : on stocke le code sur la commande (colonne sql/54). La remise est
  // consommée + recalculée côté serveur au paiement (Edge → redeem_promo_code).
  const promo = (promoCode ?? '').trim().toUpperCase();
  if (promo) payload.promo_code = promo;

  // FIX RLS (incident 2026-07-23) : la lecture publique de `orders` est fermée (fuite PII).
  // On NE relit PAS la ligne (`.select()` = RETURNING exigerait ce droit de lecture) : l'id
  // est généré côté client et l'insertion se fait en `return=minimal`. La prise de commande
  // ne dépend donc jamais d'un droit de lecture. Cf. tests/unit/checkout.test.ts (non-régression).
  const orderId = crypto.randomUUID();
  payload.id = orderId;

  // FIX collision numéro (incident 2026-07-23, Demi Lune) : `generate_order_number`
  // peut renvoyer un numéro DÉJÀ pris (après suppression d'une commande, ou 2 commandes
  // simultanées) → 23505 « duplicate key ... orders_tenant_id_order_number_key ». On
  // régénère le numéro et on réessaie (jusqu'à 3 fois) au lieu de planter la commande.
  let error: { code?: string; message?: string } | null = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    payload.order_number = orderNumber;
    ({ error } = await supabase.from('orders').insert(payload));
    if (!error) break;
    const isDupNumber =
      error.code === '23505' || /order_number|duplicate key/i.test(error.message ?? '');
    if (!isDupNumber) break; // autre erreur → on ne réessaie pas
    orderNumber = await nextOrderNumber(tenantId); // numéro frais puis nouvelle tentative
  }

  if (error) {
    console.warn('[checkout] création commande échouée:', error.message);
    return null;
  }

  const placed: PlacedOrder = {
    id: orderId,
    orderNumber,
    totalCents: total,
    pickupTime,
    pickupDate,
  };
  // FIX (incident 24/07) : la table réelle `order_items` a les colonnes `product_name`
  // et `quantity` (comme le legacy), PAS `name_snapshot`/`qty` → sans ça, la commande
  // était créée mais SANS ses lignes (« Could not find the 'name_snapshot' column »)
  // et la cuisine ne voyait pas le contenu.
  const rows = lines.map((l) => ({
    order_id: orderId,
    product_name: l.name,
    quantity: l.quantity,
    unit_price_cents: l.unitPriceCents,
    // Options groupées (parité ticket legacy) — additif, ignoré si la colonne n'existe pas encore.
    options_json: l.optionLines && l.optionLines.length ? l.optionLines : null,
  }));
  let { error: itemsError } = await supabase.from('order_items').insert(rows);
  // Repli défensif : si la colonne options_json n'est pas encore en base (migration non passée),
  // on réinsère sans elle → aucune régression sur la prise de commande.
  if (itemsError && /options_json/.test(itemsError.message ?? '')) {
    const bare = rows.map((r) => ({
      order_id: r.order_id,
      product_name: r.product_name,
      quantity: r.quantity,
      unit_price_cents: r.unit_price_cents,
    }));
    ({ error: itemsError } = await supabase.from('order_items').insert(bare));
  }
  if (itemsError) {
    console.warn('[checkout] création lignes commande échouée:', itemsError.message);
  }

  return placed;
}

/**
 * Enregistre les consentements marketing du client (RGPD) pour exploitation CRM.
 * Best-effort : ne bloque JAMAIS la commande (une RPC absente/échouée est ignorée).
 *  - Resto  → `upsert_customer_tenant` (RPC existante, additive : un opt-in ne se perd jamais).
 *  - Réseau → `set_network_consent` (RPC dédiée, cf. sql/48_checkout_consent.sql).
 */
export async function saveCheckoutConsent(
  tenantId: string,
  email: string | null | undefined,
  marketing: boolean,
  network: boolean,
): Promise<void> {
  const e = (email ?? '').trim().toLowerCase();
  if (!tenantId || !e || (!marketing && !network)) return;
  try {
    await supabase.rpc('upsert_customer_tenant', {
      p_email: e,
      p_tenant_id: tenantId,
      p_marketing_consent: marketing,
      p_loyalty_opt_in: false,
    });
  } catch {
    /* best-effort */
  }
  if (network) {
    try {
      await supabase.rpc('set_network_consent', { p_email: e, p_consent: true });
    } catch {
      /* best-effort : RPC réseau optionnelle (cf. sql/48) */
    }
  }
}
